import {
  decideAfter,
  decideBefore,
  decideLatest,
  findContainingSegment,
  findLiveTailSegment
} from './coverage';
import type { FeedSegment, LocalPageResult } from './coverage';
import { compareSortOrders } from './ids';
import type {
  AuthorId,
  Channel,
  ChannelId,
  Emoji,
  EmojiId,
  EntityBundle,
  MessageId,
  SortOrder,
  Profile,
  SyncCursor,
  WireMessage,
  WorkspaceId
} from './types';

/** Configures in-memory mirror retention for resident conversations and warm rows. */
export interface FeedMirrorOptions {
  readonly requireCompleteEntities?: boolean;
  readonly residentConversationLimit: number;
  readonly warmRowLimit: number;
}

/** Marks an inclusive sort-order range whose rows are warm in memory. */
export interface WarmCoverageRange {
  readonly end: SortOrder;
  readonly start: SortOrder;
}

/** Hydrates one conversation mirror from a durable read and its referenced entities. */
export interface FeedMirrorHydration {
  readonly coverage: readonly WarmCoverageRange[];
  readonly entities: EntityBundle;
  readonly metaRevision?: number;
  readonly messages: readonly WireMessage[];
  readonly segments: readonly FeedSegment[];
  readonly syncCursor: SyncCursor;
}

interface ConversationMirror {
  readonly accountId: string;
  readonly conversationId: ChannelId;
  coverage: WarmCoverageRange[];
  metaRevision: number | undefined;
  rows: Map<MessageId, WireMessage>;
  segments: FeedSegment[];
  syncCursor: SyncCursor;
}

interface EntityRefs {
  readonly profileIds: Set<AuthorId>;
  readonly channelIds: Set<ChannelId>;
  readonly emojiIds: Set<EmojiId>;
}

/** Maintains warm in-memory feed rows, coverage, and entities for synchronous reads. */
export class FeedMirror {
  private readonly entityChannels = new Map<string, Channel>();
  private readonly entityEmoji = new Map<string, Emoji>();
  private readonly entityProfiles = new Map<string, Profile>();
  private readonly mirror = new Map<string, ConversationMirror>();
  private readonly requireCompleteEntities: boolean;
  private readonly residentConversationLimit: number;
  private readonly warmRowLimit: number;

  /** Creates an empty mirror with bounded resident conversations and warm rows. */
  constructor(options: FeedMirrorOptions) {
    this.requireCompleteEntities = options.requireCompleteEntities ?? true;
    this.residentConversationLimit = options.residentConversationLimit;
    this.warmRowLimit = options.warmRowLimit;
  }

  /** Returns a before page from warm state, or `undefined` when rows or entities are incomplete. */
  readBefore(
    accountId: string,
    conversationId: ChannelId,
    boundary: SortOrder,
    count: number
  ): LocalPageResult | undefined {
    const mirror = this.getResidentMirror(accountId, conversationId);
    if (!mirror || mirror.metaRevision === undefined) return undefined;
    return this.readBeforeMirror(mirror, boundary, count);
  }

  /** Returns an after page from warm state, or `undefined` when rows or entities are incomplete. */
  readAfter(
    accountId: string,
    conversationId: ChannelId,
    boundary: SortOrder,
    count: number
  ): LocalPageResult | undefined {
    const mirror = this.getResidentMirror(accountId, conversationId);
    if (!mirror || mirror.metaRevision === undefined) return undefined;
    return this.readAfterMirror(mirror, boundary, count);
  }

  /** Returns a latest page from warm live-tail state, or `undefined` when warm coverage is incomplete. */
  readLatest(accountId: string, conversationId: ChannelId, count: number): LocalPageResult | undefined {
    const mirror = this.getResidentMirror(accountId, conversationId);
    if (!mirror || mirror.metaRevision === undefined) return undefined;
    return this.readLatestMirror(mirror, count);
  }

  /** Replaces committed coverage for a conversation and warms the supplied rows and entities. */
  hydrate(accountId: string, conversationId: ChannelId, hydration: FeedMirrorHydration): void {
    if (hydration.metaRevision === undefined) {
      this.dropConversation(accountId, conversationId);
      return;
    }
    this.applyCommittedSegments(
      accountId,
      conversationId,
      hydration.segments,
      hydration.syncCursor,
      hydration.metaRevision
    );
    this.addRows(accountId, conversationId, hydration.messages, hydration.entities, hydration.coverage);
  }

  /** Updates committed segments and sync cursor for a conversation without changing warm rows. */
  applyCommittedSegments(
    accountId: string,
    conversationId: ChannelId,
    segments: readonly FeedSegment[],
    syncCursor: SyncCursor,
    metaRevision: number
  ): void {
    const mirror = this.ensureConversationMirror(accountId, conversationId);
    mirror.segments = segments.map((segment) => ({ ...segment }));
    mirror.syncCursor = syncCursor;
    mirror.metaRevision = metaRevision;
  }

  /** Warms rows, referenced entities, and coverage; row eviction drops coverage that is no longer complete. */
  addRows(
    accountId: string,
    conversationId: ChannelId,
    messages: readonly WireMessage[],
    entities: EntityBundle,
    coverage: readonly WarmCoverageRange[]
  ): void {
    const mirror = this.ensureConversationMirror(accountId, conversationId);
    let evicted = false;

    for (const message of messages) {
      if (mirror.rows.has(message.id)) {
        mirror.rows.delete(message.id);
      }
      mirror.rows.set(message.id, message);
      while (mirror.rows.size > this.warmRowLimit) {
        const oldest = mirror.rows.keys().next().value;
        if (oldest === undefined) break;
        mirror.rows.delete(oldest);
        evicted = true;
      }
    }

    this.cacheReferencedEntities(accountId, messages, entities);
    const retainedNewMessages = messages.every((message) => mirror.rows.has(message.id));
    const retainedCoverage = retainedNewMessages ? coverage : [];
    mirror.coverage = coalesceWarmCoverage([...(evicted ? [] : mirror.coverage), ...retainedCoverage]);
  }

  /** Removes warm rows by id and shrinks warm coverage around any deleted rows. */
  dropRows(accountId: string, conversationId: ChannelId, messageIds: readonly MessageId[]): void {
    if (messageIds.length === 0) return;
    const mirror = this.mirror.get(conversationMirrorKey(accountId, conversationId));
    if (!mirror) return;

    const removedSortOrders: SortOrder[] = [];
    for (const id of messageIds) {
      const message = mirror.rows.get(id);
      if (!message) continue;
      mirror.rows.delete(id);
      removedSortOrders.push(message.sortOrder);
    }
    if (removedSortOrders.length > 0) {
      mirror.coverage = pruneWarmCoverage(mirror, removedSortOrders);
    }
  }

  /** Returns the committed revision represented by a resident conversation mirror. */
  metaRevision(accountId: string, conversationId: ChannelId): number | undefined {
    return this.mirror.get(conversationMirrorKey(accountId, conversationId))?.metaRevision;
  }

  /** Drops warm conversation state for one account/conversation pair. */
  dropConversation(accountId: string, conversationId: ChannelId): void {
    this.mirror.delete(conversationMirrorKey(accountId, conversationId));
  }

  /** Caches a workspace-scoped entity bundle for later synchronous reference reads. */
  cacheEntities(accountId: string, workspaceId: WorkspaceId, bundle: EntityBundle): void {
    for (const profile of bundle.profiles) {
      this.entityProfiles.set(entityMirrorKey(accountId, workspaceId, profile.id), profile);
    }
    for (const channel of bundle.channels) {
      this.entityChannels.set(entityMirrorKey(accountId, workspaceId, channel.id), channel);
    }
    for (const emoji of bundle.emoji) {
      this.entityEmoji.set(entityMirrorKey(accountId, workspaceId, emoji.id), emoji);
    }
  }

  /** Drops all warm conversation and entity state for an account. */
  dropAccount(accountId: string): void {
    for (const [key, mirror] of this.mirror) {
      if (mirror.accountId === accountId) this.mirror.delete(key);
    }
    for (const key of this.entityProfiles.keys()) {
      if (entityMirrorKeyBelongsToAccount(key, accountId)) this.entityProfiles.delete(key);
    }
    for (const key of this.entityChannels.keys()) {
      if (entityMirrorKeyBelongsToAccount(key, accountId)) this.entityChannels.delete(key);
    }
    for (const key of this.entityEmoji.keys()) {
      if (entityMirrorKeyBelongsToAccount(key, accountId)) this.entityEmoji.delete(key);
    }
  }

  /** Clears all in-memory mirror state. */
  clear(): void {
    this.mirror.clear();
    this.entityProfiles.clear();
    this.entityChannels.clear();
    this.entityEmoji.clear();
  }

  private readBeforeMirror(
    mirror: ConversationMirror,
    boundary: SortOrder,
    count: number
  ): LocalPageResult | undefined {
    const segment = findContainingSegment(mirror.segments, boundary);
    if (!segment || segment.stale) {
      return decideBefore(segment, [], emptyEntityBundle(), mirror.syncCursor);
    }
    if (count === 0) {
      return decideBefore(segment, [], emptyEntityBundle(), mirror.syncCursor);
    }

    const rows = this.warmRowsInRange(
      mirror,
      segment.oldestSortOrder,
      boundary,
      true,
      false
    );
    if (rows.length === 0) {
      if (compareSortOrders(boundary, segment.oldestSortOrder) <= 0) {
        return decideBefore(segment, [], emptyEntityBundle(), mirror.syncCursor);
      }
      return undefined;
    }

    const messages = rows.slice(Math.max(0, rows.length - count));
    const coverageStart = messages.length < count ? segment.oldestSortOrder : messages[0]?.sortOrder;
    if (!coverageStart || !this.hasWarmCoverage(mirror, coverageStart, boundary)) return undefined;

    const entities = this.readReferencedEntitiesSync(mirror.accountId, messages);
    if (!entities) return undefined;
    this.touchWarmRows(mirror, messages);
    return decideBefore(segment, messages, entities, mirror.syncCursor);
  }

  private readAfterMirror(
    mirror: ConversationMirror,
    boundary: SortOrder,
    count: number
  ): LocalPageResult | undefined {
    const segment = findContainingSegment(mirror.segments, boundary);
    if (!segment || segment.stale) {
      return decideAfter(segment, [], emptyEntityBundle(), mirror.syncCursor);
    }
    if (count === 0) {
      return decideAfter(segment, [], emptyEntityBundle(), mirror.syncCursor);
    }

    const rows = this.warmRowsInRange(
      mirror,
      boundary,
      segment.newestSortOrder,
      false,
      true
    );
    if (rows.length === 0) {
      if (compareSortOrders(boundary, segment.newestSortOrder) >= 0) {
        return decideAfter(segment, [], emptyEntityBundle(), mirror.syncCursor);
      }
      return undefined;
    }

    const messages = rows.slice(0, count);
    const coverageEnd = messages.length < count ? segment.newestSortOrder : messages.at(-1)?.sortOrder;
    if (!coverageEnd || !this.hasWarmCoverage(mirror, boundary, coverageEnd)) return undefined;

    const entities = this.readReferencedEntitiesSync(mirror.accountId, messages);
    if (!entities) return undefined;
    this.touchWarmRows(mirror, messages);
    return decideAfter(segment, messages, entities, mirror.syncCursor);
  }

  private readLatestMirror(mirror: ConversationMirror, count: number): LocalPageResult | undefined {
    const segment = findLiveTailSegment(mirror.segments);
    if (!segment || segment.stale) {
      return decideLatest(mirror.segments, [], emptyEntityBundle(), mirror.syncCursor);
    }
    if (count === 0) {
      return decideLatest(mirror.segments, [], emptyEntityBundle(), mirror.syncCursor);
    }

    const rows = this.warmRowsInRange(
      mirror,
      segment.oldestSortOrder,
      segment.newestSortOrder,
      true,
      true
    );
    if (rows.length === 0) return undefined;

    const messages = rows.slice(Math.max(0, rows.length - count));
    const coverageStart = messages.length < count ? segment.oldestSortOrder : messages[0]?.sortOrder;
    if (!coverageStart || !this.hasWarmCoverage(mirror, coverageStart, segment.newestSortOrder)) return undefined;

    const entities = this.readReferencedEntitiesSync(mirror.accountId, messages);
    if (!entities) return undefined;
    this.touchWarmRows(mirror, messages);
    return decideLatest(mirror.segments, messages, entities, mirror.syncCursor);
  }

  private warmRowsInRange(
    mirror: ConversationMirror,
    start: SortOrder,
    end: SortOrder,
    includeStart: boolean,
    includeEnd: boolean
  ): WireMessage[] {
    return [...mirror.rows.values()]
      .filter((message) => {
        const startOrder = compareSortOrders(message.sortOrder, start);
        const endOrder = compareSortOrders(message.sortOrder, end);
        return (includeStart ? startOrder >= 0 : startOrder > 0)
          && (includeEnd ? endOrder <= 0 : endOrder < 0);
      })
      .sort((left, right) => compareSortOrders(left.sortOrder, right.sortOrder));
  }

  private hasWarmCoverage(mirror: ConversationMirror, start: SortOrder, end: SortOrder): boolean {
    return mirror.coverage.some((range) =>
      compareSortOrders(range.start, start) <= 0 && compareSortOrders(end, range.end) <= 0
    );
  }

  private touchWarmRows(mirror: ConversationMirror, messages: readonly WireMessage[]): void {
    for (const message of messages) {
      if (!mirror.rows.has(message.id)) continue;
      mirror.rows.delete(message.id);
      mirror.rows.set(message.id, message);
    }
  }

  private getResidentMirror(accountId: string, conversationId: ChannelId): ConversationMirror | undefined {
    const key = conversationMirrorKey(accountId, conversationId);
    const mirror = this.mirror.get(key);
    if (!mirror) return undefined;
    this.mirror.delete(key);
    this.mirror.set(key, mirror);
    return mirror;
  }

  private ensureConversationMirror(accountId: string, conversationId: ChannelId): ConversationMirror {
    const key = conversationMirrorKey(accountId, conversationId);
    const existing = this.mirror.get(key);
    if (existing) {
      this.mirror.delete(key);
      this.mirror.set(key, existing);
      return existing;
    }

    const mirror: ConversationMirror = {
      accountId,
      conversationId,
      coverage: [],
      metaRevision: undefined,
      rows: new Map(),
      segments: [],
      syncCursor: ''
    };
    this.mirror.set(key, mirror);
    this.enforceResidentConversationLimit();
    return mirror;
  }

  private enforceResidentConversationLimit(): void {
    while (this.mirror.size > this.residentConversationLimit) {
      const oldest = this.mirror.keys().next().value;
      if (oldest === undefined) return;
      this.mirror.delete(oldest);
    }
  }

  private cacheReferencedEntities(
    accountId: string,
    messages: readonly WireMessage[],
    bundle: EntityBundle
  ): void {
    const refsByWorkspace = referencedEntitiesByWorkspace(messages);
    const profilesById = entityMap(bundle.profiles);
    const channelsById = entityMap(bundle.channels);
    const emojiById = entityMap(bundle.emoji);

    for (const [workspaceId, refs] of refsByWorkspace) {
      for (const id of refs.profileIds) {
        const record = profilesById.get(id);
        if (record) this.entityProfiles.set(entityMirrorKey(accountId, workspaceId, id), record);
      }
      for (const id of refs.channelIds) {
        const record = channelsById.get(id);
        if (record) this.entityChannels.set(entityMirrorKey(accountId, workspaceId, id), record);
      }
      for (const id of refs.emojiIds) {
        const record = emojiById.get(id);
        if (record) this.entityEmoji.set(entityMirrorKey(accountId, workspaceId, id), record);
      }
    }
  }

  /** Returns referenced entities when every message reference is cached, otherwise `undefined`. */
  readReferencedEntitiesSync(
    accountId: string,
    messages: readonly WireMessage[]
  ): EntityBundle | undefined {
    if (!this.requireCompleteEntities) return emptyEntityBundle();

    const refsByWorkspace = referencedEntitiesByWorkspace(messages);
    const profiles = new Map<AuthorId, Profile>();
    const channels = new Map<ChannelId, Channel>();
    const emoji = new Map<EmojiId, Emoji>();

    for (const [workspaceId, refs] of refsByWorkspace) {
      for (const id of refs.profileIds) {
        const record = this.entityProfiles.get(entityMirrorKey(accountId, workspaceId, id));
        if (!record) return undefined;
        profiles.set(id, record);
      }
      for (const id of refs.channelIds) {
        const record = this.entityChannels.get(entityMirrorKey(accountId, workspaceId, id));
        if (!record) return undefined;
        channels.set(id, record);
      }
      for (const id of refs.emojiIds) {
        const record = this.entityEmoji.get(entityMirrorKey(accountId, workspaceId, id));
        if (!record) return undefined;
        emoji.set(id, record);
      }
    }

    return {
      profiles: [...profiles.values()],
      channels: [...channels.values()],
      emoji: [...emoji.values()]
    };
  }
}

function conversationMirrorKey(accountId: string, conversationId: ChannelId): string {
  return `${accountId}:${conversationId}`;
}

function entityMirrorKey(accountId: string, workspaceId: WorkspaceId, id: string): string {
  return `${accountId}\x1f${workspaceId}\x1f${id}`;
}

function entityMirrorKeyBelongsToAccount(key: string, accountId: string): boolean {
  return key.startsWith(`${accountId}\x1f`);
}

function referencedEntitiesByWorkspace(messages: readonly WireMessage[]): Map<WorkspaceId, EntityRefs> {
  const byWorkspace = new Map<WorkspaceId, EntityRefs>();

  for (const message of messages) {
    let refs = byWorkspace.get(message.workspaceId);
    if (!refs) {
      refs = emptyEntityRefs();
      byWorkspace.set(message.workspaceId, refs);
    }

    refs.profileIds.add(message.authorId);
    refs.channelIds.add(message.channelId);
    for (const reaction of message.reactions) {
      refs.emojiIds.add(reaction.emojiId);
    }
  }

  return byWorkspace;
}

function emptyEntityRefs(): EntityRefs {
  const profileIds = new Set<AuthorId>();
  const channelIds = new Set<ChannelId>();
  const emojiIds = new Set<EmojiId>();
  return { profileIds, channelIds, emojiIds };
}

function entityMap<TId extends string, TRecord extends { readonly id: TId }>(
  records: readonly TRecord[]
): Map<TId, TRecord> {
  return new Map(records.map((record) => [record.id, record]));
}

function emptyEntityBundle(): EntityBundle {
  return {
    profiles: [],
    channels: [],
    emoji: []
  };
}

function coalesceWarmCoverage(ranges: readonly WarmCoverageRange[]): WarmCoverageRange[] {
  const sorted = [...ranges].sort((left, right) => compareSortOrders(left.start, right.start));
  const coalesced: WarmCoverageRange[] = [];

  for (const range of sorted) {
    const previous = coalesced.at(-1);
    if (previous && compareSortOrders(range.start, previous.end) <= 0) {
      coalesced[coalesced.length - 1] = {
        start: previous.start,
        end: compareSortOrders(previous.end, range.end) >= 0 ? previous.end : range.end
      };
    } else {
      coalesced.push(range);
    }
  }

  return coalesced;
}

function pruneWarmCoverage(
  mirror: ConversationMirror,
  removedSortOrders: readonly SortOrder[]
): WarmCoverageRange[] {
  return coalesceWarmCoverage(mirror.coverage.flatMap((range) =>
    pruneWarmCoverageRange(range, mirror.rows, removedSortOrders)
  ));
}

function pruneWarmCoverageRange(
  range: WarmCoverageRange,
  rows: ReadonlyMap<MessageId, WireMessage>,
  removedSortOrders: readonly SortOrder[]
): WarmCoverageRange[] {
  const removedInRange = removedSortOrders.filter((sortOrder) => warmRangeContains(range, sortOrder));
  if (removedInRange.length === 0) return [range];

  const survivors = [...rows.values()]
    .map((message) => message.sortOrder)
    .filter((sortOrder) => warmRangeContains(range, sortOrder))
    .sort(compareSortOrders);
  const firstSurvivor = survivors[0];
  if (!firstSurvivor) return [];

  const runs: WarmCoverageRange[] = [];
  let runStart = firstSurvivor;
  let runEnd = firstSurvivor;

  for (const sortOrder of survivors.slice(1)) {
    if (hasRemovedSortOrderBetween(removedInRange, runEnd, sortOrder)) {
      runs.push({ start: runStart, end: runEnd });
      runStart = sortOrder;
    }
    runEnd = sortOrder;
  }
  runs.push({ start: runStart, end: runEnd });
  return runs;
}

function warmRangeContains(range: WarmCoverageRange, sortOrder: SortOrder): boolean {
  return compareSortOrders(range.start, sortOrder) <= 0 && compareSortOrders(sortOrder, range.end) <= 0;
}

function hasRemovedSortOrderBetween(
  removedSortOrders: readonly SortOrder[],
  left: SortOrder,
  right: SortOrder
): boolean {
  return removedSortOrders.some((sortOrder) =>
    compareSortOrders(left, sortOrder) < 0 && compareSortOrders(sortOrder, right) < 0
  );
}
